import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectSelectedMultiProperties } from "@/redux/slices/property";
import {
  listPricingRequests,
  listPricingRequestsAcrossProperties,
  enrichRowsWithSarahReviewType,
} from "@/pages/grouppricingcalculator/ActionApis/GpcApis";
import {
  GPC_UI_SOURCE,
  SARAH_SOURCE,
} from "@/pages/grouppricingcalculator/ActionApis/types";
import type { PricingRequestListItem } from "@/pages/grouppricingcalculator/ActionApis/types";
import { logError } from "@/utils/errorHandler";

const PAGE_SIZE = 500;
// No server-side "undecided" filter, so this pages through everything
// (newest first) rather than trusting a single page not to miss an old
// pending row. Bounded rather than unbounded; logs instead of silently
// under-reporting if the bound is hit.
const MAX_PAGES_SCANNED = 4;

// GPC_UI rows (created on the GPC page itself, for testing/reference) never
// count toward the badge. SARAH's `decision` can't distinguish "pending"
// from "auto-priced, never escalated" — same reason PricingRequestsTable's
// getRequestTab doesn't use it either — so SARAH rows key off
// pendingGpcReviewType instead, which the batch call below fills in.
const isPending = (row: PricingRequestListItem): boolean => {
  if (row.requestSource === GPC_UI_SOURCE) return false;
  if (row.requestSource === SARAH_SOURCE) return !!row.pendingGpcReviewType;
  return !row.decision;
};

/** Sidebar badge for GPC: pending GPRT/SARAH requests, scoped to the properties selected on the GPC page. */
export const useGpcPendingRequestsCount = (): number => {
  const selectedProperties = useSelector(selectSelectedMultiProperties);
  const propertyKey = selectedProperties
    .map((p) => p.propertyId)
    .sort()
    .join(",");
  const [count, setCount] = useState(0);

  useEffect(() => {
    const propertyIds = propertyKey ? propertyKey.split(",") : [];
    if (propertyIds.length === 0) {
      setCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        let pending = 0;
        let page = 0;
        let totalPages = 1;
        while (page < totalPages && page < MAX_PAGES_SCANNED) {
          const params = {
            page,
            size: PAGE_SIZE,
            sort: "createdAt,desc",
          };
          const res =
            propertyIds.length > 1
              ? await listPricingRequestsAcrossProperties(propertyIds, params)
              : await listPricingRequests(propertyIds[0], params);
          if (cancelled) return;

          const rows = await enrichRowsWithSarahReviewType(res.content);
          if (cancelled) return;

          pending += rows.filter(isPending).length;
          totalPages = res.totalPages;
          page += 1;
        }
        if (cancelled) return;
        if (page < totalPages) {
          logError(
            new Error(
              `GPC pending count scan capped at ${MAX_PAGES_SCANNED} pages (${
                MAX_PAGES_SCANNED * PAGE_SIZE
              } rows) — ${totalPages - page} page(s) left unscanned`
            ),
            "Sidebar: GPC pending requests count may be an undercount"
          );
        }
        setCount(pending);
      } catch (err: unknown) {
        if (!cancelled) {
          logError(err, "Sidebar: failed to load GPC pending requests count");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [propertyKey]);

  return count;
};
